import { Box, Button, Chip, paletteColors, toast, Tooltip } from '@evaneos/b2b-ui';
import CalendarMonthOutlinedIcon from '@evaneos/b2b-ui/icons/CalendarMonthOutlined';
import { useIntl } from 'react-intl';

import { buildShareAppointmentLink } from '../../../../../resources/appointment/operations';
import {
    useGetAgentScheduleLink,
    useGetCalendarStatus,
} from '../../../../../resources/appointment/queries';
import { DossierId } from '../../../../../resources/dossier/types';
import { useLocale } from '../../../../../shared/providers/LocalizationProvider';

import { tooltipSlotProps } from './NewMessageBox';

type Props = {
    dossierId: DossierId;
};

export const ShareAppointmentLinkButton = ({ dossierId }: Props): JSX.Element | null => {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { data: calendarStatus } = useGetCalendarStatus();
    const isGoogleConnected = calendarStatus?.google === 'connected';
    const { data: agentScheduleLink } = useGetAgentScheduleLink(locale, isGoogleConnected);

    const shareLink = buildShareAppointmentLink(agentScheduleLink?.scheduleLink ?? '', dossierId);

    if (!isGoogleConnected || !shareLink) {
        return null;
    }

    const label = formatMessage({ id: 'appointment.share_link.tooltip' });

    const handleClick = () => {
        navigator.clipboard
            .writeText(shareLink)
            .then(() => {
                toast.success(formatMessage({ id: 'appointment.share_link.copy_success' }));
            })
            .catch(() => {
                toast.error(formatMessage({ id: 'global.generic_error' }));
            });
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={label} placement="top" disableInteractive slotProps={tooltipSlotProps}>
                <Button
                    onClick={handleClick}
                    variant="text"
                    color="secondary"
                    aria-label={label}
                    sx={{ width: '40px', minWidth: '40px' }}
                    data-spec-id="appointment-share-link-button"
                >
                    <CalendarMonthOutlinedIcon />
                </Button>
            </Tooltip>
            <Chip
                component="strong"
                size="small"
                label={formatMessage({ id: 'global.new' })}
                variant="rounded"
                sx={{
                    backgroundColor: 'background.information',
                    color: paletteColors.blue[700],
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    height: 'auto',
                    py: 0.25,
                }}
            />
        </Box>
    );
};
